import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { finalize, switchMap, catchError } from 'rxjs/operators';
import {
  DashboardLayoutService,
  WidgetCatalogEntry,
} from '../../../core/services/dashboard-layout.service';
import { ToasterService } from '../../../core/services/toaster.service';

export interface DashboardAddWidgetDialogData {
  dashboardApiId: number | null;
  isMixed: boolean;
  pageId: number | null;
  alreadyExistingKeys: string[];
  currentWidgets: Array<{
    id: number;
    pageId: number | null;
    colSpan: number;
  }>;
  dashboardName: string;
}

@Component({
  standalone: true,
  selector: 'app-dashboard-add-widget-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
  ],
  template: `
    <div class="relative overflow-hidden bg-slate-50/95 dark:bg-surface-0/95 border border-slate-200/80 dark:border-white/10 shadow-2xl rounded-[2.5rem] flex flex-col max-h-[85vh] md:max-h-[90vh]">
      
      <!-- Top Decorative Gradient Glow -->
      <div class="absolute top-0 left-1/4 right-1/4 h-24 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-3xl pointer-events-none"></div>

      <!-- Header -->
      <div class="px-6 py-5 border-b border-slate-200/60 dark:border-white/5 flex items-center justify-between bg-white/40 dark:bg-white/5 relative z-10">
        <div>
          <h2 class="text-xl font-bold text-slate-800 dark:text-white">
            Manage Dashboard Widgets
          </h2>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Toggle checkboxes to add or remove widgets from your dashboard layout
          </p>
        </div>
        <button
          (click)="onCancel()"
          class="p-2 rounded-full hover:bg-slate-200/50 dark:hover:bg-white/10 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
          type="button"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <mat-dialog-content class="flex-1 overflow-y-auto px-6 py-6 !m-0 space-y-6 relative z-10 custom-scrollbar">
        
        <!-- Search Widgets -->
        <div class="relative">
          <input
            type="text"
            [value]="searchQuery()"
            (input)="onSearchInput($event)"
            placeholder="Search catalog widgets..."
            class="w-full bg-white dark:bg-slate-900/50 border border-slate-200/80 dark:border-white/10 rounded-2xl px-4 py-3 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          />
        </div>

        <!-- Loader / Empty States -->
        @if (loadingWidgets()) {
          <div class="flex flex-col items-center justify-center py-20 gap-3">
            <div class="animate-spin rounded-full h-8 w-8 border-2 border-slate-300 dark:border-slate-700 border-t-blue-600"></div>
            <span class="text-xs text-slate-500 dark:text-slate-400">Loading catalog widgets...</span>
          </div>
        } @else if (filteredWidgets().length === 0) {
          <div class="p-12 rounded-[2rem] border border-dashed border-slate-200 dark:border-white/10 text-center bg-white/20 dark:bg-white/5">
            <p class="text-xs text-slate-500 dark:text-slate-400">
              No widgets available for selection.
            </p>
          </div>
        } @else {
          <!-- Widgets List (Responsive Grid) -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[45vh] overflow-y-auto pr-1 custom-scrollbar">
            @for (widget of filteredWidgets(); track widget.id) {
              <div
                (click)="toggleWidgetSelection(widget)"
                [ngClass]="{
                  'border-blue-500/80 bg-blue-50/40 dark:bg-blue-950/15 shadow-inner shadow-blue-500/5': isWidgetSelected(widget.id),
                  'border-slate-200/60 dark:border-white/5 bg-white/70 dark:bg-white/5 hover:border-slate-300 dark:hover:border-white/15 hover:bg-white/90 dark:hover:bg-white/10 hover:shadow-md': !isWidgetSelected(widget.id)
                }"
                class="p-4 rounded-2xl border cursor-pointer select-none transition-all duration-300 flex items-center justify-between gap-4 group hover:scale-[1.01]"
              >
                <div class="flex items-center gap-3.5 min-w-0">
                  <!-- Contextual SVG Preview Icons -->
                  <div
                    [ngClass]="{
                      'bg-blue-500/10 text-blue-600 dark:bg-blue-400/10 dark:text-blue-400': isWidgetSelected(widget.id),
                      'bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400': !isWidgetSelected(widget.id)
                    }"
                    class="w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-300 shrink-0"
                  >
                    <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      @if (widget.widget_key.includes('chart') || widget.widget_key.includes('types') || widget.widget_key.includes('performance') || widget.widget_key.includes('growth') || widget.widget_key.includes('stats')) {
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      } @else if (widget.widget_key.includes('detail') || widget.widget_key.includes('list') || widget.widget_key.includes('summary') || widget.widget_key.includes('table')) {
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      } @else {
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      }
                    </svg>
                  </div>

                  <div class="min-w-0">
                    <div class="font-bold text-xs md:text-sm text-slate-800 dark:text-slate-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {{ widget.name }}
                    </div>
                    <div class="text-[10px] md:text-xs text-slate-400 dark:text-slate-500 mt-0.5 truncate">
                      {{ widget.widget_key }}
                    </div>
                  </div>
                </div>

                <!-- Custom Checkbox -->
                <div
                  [ngClass]="{
                    'bg-blue-600 border-blue-600 text-white scale-110 shadow-md shadow-blue-500/20': isWidgetSelected(widget.id),
                    'border-slate-300 dark:border-white/20 bg-transparent text-transparent': !isWidgetSelected(widget.id)
                  }"
                  class="w-5 h-5 rounded-md border flex items-center justify-center transition-all duration-300 shrink-0"
                >
                  <svg class="w-3.5 h-3.5 stroke-[3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            }
          </div>
        }
      </mat-dialog-content>

      <!-- Footer Actions -->
      <div class="px-6 py-4 border-t border-slate-200/60 dark:border-white/5 bg-white/40 dark:bg-white/5 flex items-center justify-end gap-3 relative z-10">
        <button
          type="button"
          [disabled]="submitting()"
          (click)="onCancel()"
          class="px-5 py-3 rounded-2xl text-sm font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 transition-all flex items-center gap-2"
        >
          Cancel
        </button>
        <button
          type="button"
          (click)="onSubmit()"
          [disabled]="submitting() || loadingWidgets()"
          [ngClass]="{
            'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/35 hover:-translate-y-0.5 cursor-pointer': !(submitting() || loadingWidgets()),
            'bg-slate-200 dark:bg-slate-800/80 text-slate-400 dark:text-slate-500 cursor-not-allowed': submitting() || loadingWidgets()
          }"
          class="px-6 py-3 rounded-2xl text-sm font-bold transition-all duration-300 flex items-center justify-center min-w-[140px]"
        >
          @if (submitting()) {
            <span class="flex items-center gap-2">
              <svg class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Applying...
            </span>
          } @else {
            Apply Changes
          }
        </button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host {
      display: block;
      border-radius: 2.5rem;
      overflow: hidden;
    }
    ::ng-deep .mat-mdc-dialog-container .mdc-dialog__surface {
      background: transparent !important;
      box-shadow: none !important;
    }
    ::ng-deep .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    ::ng-deep .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }
    ::ng-deep .custom-scrollbar::-webkit-scrollbar-thumb {
      background: rgba(156, 163, 175, 0.25);
      border-radius: 9999px;
    }
    ::ng-deep .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: rgba(156, 163, 175, 0.45);
    }
  `]
})
export class DashboardAddWidgetDialogComponent implements OnInit {
  data = inject<DashboardAddWidgetDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<DashboardAddWidgetDialogComponent>);
  private layoutService = inject(DashboardLayoutService);
  private toaster = inject(ToasterService);

  // State Signals
  widgets = signal<WidgetCatalogEntry[]>([]);
  filteredWidgets = signal<WidgetCatalogEntry[]>([]);
  selectedWidgetIds = signal<Set<number>>(new Set());
  searchQuery = signal<string>('');
  loadingWidgets = signal<boolean>(false);
  submitting = signal<boolean>(false);

  ngOnInit() {
    this.selectedWidgetIds.set(new Set(this.data.currentWidgets.map(w => w.id)));
    this.loadCatalogWidgets();
  }

  private loadCatalogWidgets() {
    this.loadingWidgets.set(true);

    if (this.data.isMixed) {
      this.layoutService
        .getCatalogProducts()
        .pipe(
          switchMap((catalog) => {
            const allPages = catalog.flatMap((p: any) => p.pages || []);
            const requests = allPages.map((page: any) =>
              this.layoutService.getWidgetCatalog(page.id, 'web').pipe(catchError(() => of([])))
            );
            return forkJoin(requests);
          }),
          finalize(() => this.loadingWidgets.set(false)),
          catchError(() => {
            this.toaster.error('Failed to fetch widgets catalog.');
            return of([]);
          })
        )
        .subscribe((results) => {
          const uniqueWidgets = new Map<string, WidgetCatalogEntry>();
          results.flat().forEach((widget) => {
            if (widget && widget.widget_key) {
              uniqueWidgets.set(widget.widget_key, widget);
            }
          });
          const combined = Array.from(uniqueWidgets.values());
          this.widgets.set(combined);
          this.applyFilter();
        });
    } else {
      const pageId = this.data.pageId;
      if (!pageId) {
        this.loadingWidgets.set(false);
        this.toaster.error('No target page ID provided for widget loading.');
        return;
      }

      this.layoutService
        .getWidgetCatalog(pageId, 'web')
        .pipe(
          finalize(() => this.loadingWidgets.set(false)),
          catchError(() => {
            this.toaster.error('Failed to fetch widgets catalog.');
            return of([]);
          })
        )
        .subscribe((catalogWidgets) => {
          this.widgets.set(catalogWidgets);
          this.applyFilter();
        });
    }
  }

  onSearchInput(event: Event) {
    const query = (event.target as HTMLInputElement).value;
    this.searchQuery.set(query);
    this.applyFilter();
  }

  private applyFilter() {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) {
      this.filteredWidgets.set(this.widgets());
      return;
    }
    const filtered = this.widgets().filter(
      (w) =>
        w.name.toLowerCase().includes(query) ||
        w.widget_key.toLowerCase().includes(query)
    );
    this.filteredWidgets.set(filtered);
  }

  toggleWidgetSelection(widget: WidgetCatalogEntry) {
    this.selectedWidgetIds.update((set) => {
      const newSet = new Set(set);
      if (newSet.has(widget.id)) {
        newSet.delete(widget.id);
      } else {
        newSet.add(widget.id);
      }
      return newSet;
    });
  }

  isWidgetSelected(id: number): boolean {
    return this.selectedWidgetIds().has(id);
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  onSubmit() {
    this.submitting.set(true);

    const initialIds = new Set(this.data.currentWidgets.map(w => w.id));
    const finalIds = this.selectedWidgetIds();

    if (!this.data.dashboardApiId) {
      // Pristine Upgrade Flow
      const checkedWidgets = Array.from(finalIds).map((id) => {
        const widgetMeta = this.widgets().find((w) => w.id === id);
        return {
          widget_id: Number(id),
          source_page_id: widgetMeta?.page_id ? Number(widgetMeta.page_id) : null,
          col_span: widgetMeta?.default_cols ?? 12,
          row_span: 1,
          custom_config: {}
        };
      });

      const bodyDto = {
        name: this.data.dashboardName,
        type: 'product_page' as const,
        platform: 'web' as const,
        page_id: Number(this.data.pageId),
        widget_ids: checkedWidgets
      };

      this.layoutService.createDashboard(bodyDto)
        .pipe(
          finalize(() => this.submitting.set(false)),
          catchError((err) => {
            console.error('Error creating custom dashboard layout:', err);
            this.toaster.error(err?.error?.message || 'Failed to create layout.');
            return of(null);
          })
        )
        .subscribe((res) => {
          if (res) {
            this.toaster.success('Successfully customized and updated your dashboard widgets!');
            this.dialogRef.close(true);
          }
        });
      return;
    }

    // Delta sync: Identify additions and deletions
    const toAdd = Array.from(finalIds).filter(id => !initialIds.has(id));
    const toDelete = Array.from(initialIds).filter(id => !finalIds.has(id));

    // Construct parallel add requests
    const addRequests = toAdd.map((id) => {
      const widgetMeta = this.widgets().find((w) => w.id === id);
      const payload = {
        widget_id: Number(id),
        source_page_id: widgetMeta?.page_id ? Number(widgetMeta.page_id) : null,
        col_span: widgetMeta?.default_cols ?? 12,
        row_span: 1,
        custom_config: {}
      };
      return this.layoutService.addWidget(this.data.dashboardApiId!, payload).pipe(
        catchError((err) => {
          console.error(`Error adding widget ID ${id}:`, err);
          return of(null);
        })
      );
    });

    // Construct parallel delete requests
    const deleteRequests = toDelete.map((id) => {
      return this.layoutService.deleteWidget(this.data.dashboardApiId!, id).pipe(
        catchError((err) => {
          console.error(`Error deleting widget ID ${id}:`, err);
          return of(null);
        })
      );
    });

    forkJoin([...addRequests, ...deleteRequests])
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe(() => {
        this.toaster.success('Successfully synchronized your dashboard widgets!');
        this.dialogRef.close(true);
      });
  }
}
